<template>
    <div class="ventas_tab report_theme_container" data-theme="ventas">
        <TabHeader
            title="Ventas"
            description="Analiza la tendencia y composición de tus ventas"
            :filter-period="filterPeriod"
            :filter-options="visiblePeriodOptions"
            :end-month="endMonth"
            :get-now="getNow"
            calendar-mode="range"
            :date-range="calendarRange"
            :year-range="yearRange"
            :table-title="chartsSectionTitle"
            table-title-collapsible
            :table-expanded="chartsExpanded"
            @toggle-table="toggleCharts"
            @update-filter-period="updateFilterPeriod"
            @update-end-month="updateEndMonth"
            @update-date-range="updateDateRange"
            @update-year-range="updateYearRange">
            <template #center-sub-nav>
                <div class="sub_nav_filters">
                    <button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
                        :class="{ 'is-active': activeSubFilter === filter.id }"
                        :disabled="filter.disabled"
                        @click="updateActiveSubFilter(filter.id)">
                        {{ filter.label }}
                    </button>
                </div>
            </template>
            <!-- Filtro de items (cuentas/clientes/categoría) en la barra del
                header sticky → siempre visible al hacer scroll de la tabla. -->
            <template #table-bar-actions>
                <MultiSelectDropdown
                    :value="selectedItems"
                    :options="itemFilterOptions"
                    :placeholder="itemFilterPlaceholder"
                    searchable
                    align="right"
                    @input="onItemsFilterChange" />
            </template>
        </TabHeader>

        <!-- Gráficos del header: stacked bar por cliente + actividad (count
            de clientes + ticket promedio). Colapsable para liberar alto cuando
            el foco está en la tabla; estado persiste en localStorage por tab.
            Key nueva (no la histórica de summary) para no arrastrar la
            preferencia de un layout distinto. -->
        <!-- Cuerpo de los gráficos: headless (el toggle/título vive en el header
            sticky) y controlado por `chartsExpanded`. Queda acá abajo, NO sticky,
            scrollea con el resto del contenido. -->
        <CollapsibleSection class="mt-1"
            headless
            :controlled-expanded="chartsExpanded">
            <SalesChartsRow
                :charts-data="headerChartsData"
                :is-loading="headerChartsLoading"
                :error="headerChartsError"
                :percent-mode.sync="percentMode" />
        </CollapsibleSection>

        <!-- Main Table Section -->
        <SalesEvolutionTable class="mt-2"
            :month-headers="monthHeaders"
            :table-rows="tableRows"
            :total-row="totalRow"
            :is-loading="isLoading"
            :error="error"
            :filter-period="filterPeriod"
            :comparison-enabled="activeSubFilter !== 'clientes'"
            :comparison="comparisonViewModel"
            :percent-mode="percentMode"
            @retry="fetchSalesData"
            @cell-click="openSalesAnalysis"
            @toggle-comparison="onToggleComparison"
            @comparison-select-item="onComparisonSelectItem"
            @comparison-add-year="onComparisonAddYear"
            @comparison-remove-year="onComparisonRemoveYear" />
    </div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import SalesChartsRow from "@/components/financialReports/SalesChartsRow.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import { readBoolFlag, writeBoolFlag } from "@/utils/localFlag";
import {
    FILTER_PERIOD_OPTIONS,
    TOP_CLIENTS_COUNT,
} from "@/config/reportStructure";

// Key compartida por los 3 tabs (Ventas/Costos/Gastos): la preferencia de
// expandir/colapsar los gráficos del header es global.
const CHARTS_EXPANDED_KEY = "financialReports:charts-expanded";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";
import { periodLastMonth } from "@/views/financialReports/utils/periodFormatting";
import {
    clientDetailModalConfig,
    salesAnalysisModalConfig,
} from "@/components/financialReports/salesAnalysis/salesDrawerModals";
import { capitalize } from "@/utils/string";

export default {
    name: "VentasTab",

    components: {
        TabHeader,
        CollapsibleSection,
        SalesChartsRow,
        SalesEvolutionTable,
        MultiSelectDropdown,
    },

    data() {
        return {
            filterOptions: FILTER_PERIOD_OPTIONS,
            // Panel de comparación abierto (toggle del botón de la tabla). Lo
            // trackeamos acá para refetchear la comparación al cambiar el mes
            // del header sólo si el panel está visible.
            comparisonOpen: false,
            // "% de ventas": un solo toggle (botón en SalesChartsRow) que cambia
            // tanto el chart de líneas como la tabla principal a porcentajes
            // sobre Ventas Netas. Vive acá para compartirlo entre ambos.
            percentMode: false,
            // Estado expandido de los gráficos. El toggle vive en el header sticky
            // (TabHeader), el cuerpo abajo (CollapsibleSection headless). Persistido
            // en localStorage para recordar la preferencia.
            chartsExpanded: readBoolFlag(CHARTS_EXPANDED_KEY),
        };
    },

    computed: {
        ...mapState("classification", ["categories", "itemsByCategory"]),
        ...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange", "customYearRange", "pendingDrill"]),
        ...mapState("financialReports", ["filterPeriod"]),
        ...mapState("salesReport", [
            "activeSubFilter",
            "availableCategories",
            "monthHeaders",
            "isLoading",
            "error",
            "isChartsLoading",
            "chartsError",
            "selectedItems",
            "comparisonData",
            "isComparisonLoading",
            "comparisonError",
            "comparisonYears",
            "comparisonItem",
        ]),
        // Lente activo del drawer — el header del modal puede cambiar el mes y
        // necesitamos retener account/groupBy/selectedItems para reusarlos en
        // el refetch sin leer state.x directo desde un handler.
        ...mapState("salesAnalysis", {
            analysisAccount: "account",
            analysisGroupBy: "groupBy",
            analysisSelectedItems: "selectedItems",
        }),
        ...mapGetters("salesReport", ["tableRows", "totalRow", "availableItems", "headerChartsData"]),

        getNow() {
            return moment().toDate();
        },

        // Rango activo del calendar del header. Si el store tiene `customRange`
        // poblado lo usamos tal cual; si no, derivamos un default razonable a
        // partir de endMonth + selectedPeriodRange para que el calendar abra
        // pintando "Último año" en lugar de "—" / "—". Ese default es sólo
        // visual: el fetch sigue usando `selectedPeriodRange` hasta que el
        // usuario interactúe con el calendar y dispare `updateDateRange`.
        calendarRange() {
            if (this.customRange) return this.customRange;
            const end = moment(this.endMonth).startOf("month");
            return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
        },

        // Rango de años del picker de Anual: el custom elegido, o el default de
        // Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
        // abra pintando el span real y nunca difiera de la tabla.
        yearRange() {
            return this.customYearRange || defaultYearRange(this.endMonth);
        },

        // Períodos visibles en el header. En modo comparación YTD/Anual no
        // aplican: el gráfico compara curvas mensuales/trimestrales año-contra-
        // año, así que sólo dejamos Mensual y Trimestral mientras el panel está
        // abierto. Con la tabla visible se muestran los cuatro.
        visiblePeriodOptions() {
            if (!this.comparisonOpen) return this.filterOptions;
            return this.filterOptions.filter(
                (o) => o.id === "month" || o.id === "quarter"
            );
        },

        subNavFilters() {
            const filters = [{ id: "cuentas", label: "Cuentas" }];
            this.categories.forEach(category => {
                filters.push({
                    id: category,
                    label: capitalize(category),
                    // Sin clasificación real en el período (sólo "Sin <categoría>")
                    // → la tabla mostraría sólo el total repetido y los charts una
                    // sola serie. El backend confirma cuáles tienen datos
                    // (`availableCategories`); el resto se deshabilita.
                    disabled: !this.availableCategories.includes(category),
                });
            });
            filters.push({ id: "clientes", label: "Clientes" });
            return filters;
        },

        // Título de la sección de gráficos, dinámico según el sub-filtro activo:
        // "Ventas por cuenta" / "Ventas por cliente" / "Ventas por <categoría>".
        // Tomamos el label del chip activo y lo singularizamos en minúscula —
        // los labels del dominio vienen en plural ("Cuentas", "Clientes",
        // "Tiendas"), así que quitar la "s" final cubre los casos reales.
        chartsSectionTitle() {
            const active = this.subNavFilters.find(
                (f) => f.id === this.activeSubFilter
            );
            const label = active ? active.label : "";
            const singular = label.replace(/s$/i, "").toLowerCase();
            return `Ventas por ${singular}`;
        },

        // Opciones del filtro multi-select del header: la lista completa de items
        // del sub-filtro activo (cuentas / categoría / clientes). `availableItems`
        // nunca se acota por la selección, así el dropdown siempre los ofrece
        // todos. id = label (la etiqueta es la clave en accountsData).
        itemFilterOptions() {
            return this.availableItems.map((name) => ({ id: name, label: name }));
        },

        // Placeholder dinámico según el sub-filtro: "Filtrar cuentas" /
        // "Filtrar clientes" / "Filtrar tiendas".
        itemFilterPlaceholder() {
            const active = this.subNavFilters.find((f) => f.id === this.activeSubFilter);
            const label = active ? active.label.toLowerCase() : "items";
            return `Filtrar ${label}`;
        },

        // Con selección activa los gráficos del header se derivan client-side de
        // la data de la tabla (instantáneo, sin fetch propio), así que el
        // loading/error relevante es el de la tabla; sin selección, el del
        // endpoint de gráficos.
        headerChartsLoading() {
            return this.selectedItems.length ? this.isLoading : this.isChartsLoading;
        },
        headerChartsError() {
            return this.selectedItems.length
                ? (this.error || "")
                : (this.chartsError || "");
        },

        // Lista de categorías para la card de Distribución del modal de
        // análisis — backend soporta múltiples (max 2 hoy: tiendas + marcas
        // típicamente) y devuelve una distribución por cada una. Sin
        // categorías configuradas, el card del frontend skipea su render.
        analysisGroupByForOpen() {
            return Array.isArray(this.categories) ? this.categories.slice(0, 2) : [];
        },

        // Firma reactiva consolidada — un solo trigger para refetchear tabla y
        // gráficos aunque varios filtros cambien en la misma transacción. Tabla y
        // charts del header dependen de los mismos inputs (rango + sub-filtro +
        // granularidad), así que comparten una sola signature. Incluye
        // `customRange` para que cambiar el rango via el calendar (que también
        // setea selectedPeriodRange="custom") refetchee aunque endMonth ya sea el
        // mismo.
        fetchSignature() {
            return [
                this.endMonth,
                this.selectedPeriodRange,
                this.activeSubFilter,
                this.filterPeriod,
                this.customRange && this.customRange.from,
                this.customRange && this.customRange.to,
                this.customYearRange && this.customYearRange.from,
                this.customYearRange && this.customYearRange.to,
            ].join("|");
        },

        // True cuando el sub-filtro activo es una categoría (tiendas/marcas) —
        // ni cuentas ni clientes. Sólo en ese caso la comparación filtra por
        // subcategoría.
        isCategoryFilter() {
            return this.activeSubFilter !== "cuentas"
                && this.activeSubFilter !== "clientes";
        },

        // Subcategorías de la categoría activa (botones del gráfico de
        // comparación). Vacío en cuentas/clientes.
        comparisonSubItems() {
            return this.isCategoryFilter
                ? (this.itemsByCategory[this.activeSubFilter] || [])
                : [];
        },

        // View-model que se pasa entero a SalesEvolutionTable → SalesComparisonChart
        // (sus keys matchean 1:1 las props del chart).
        comparisonViewModel() {
            return {
                series: this.comparisonData ? this.comparisonData.series : [],
                months: this.comparisonData ? this.comparisonData.months : [],
                availableYears: this.comparisonData
                    ? this.comparisonData.availableYears
                    : [],
                isLoading: this.isComparisonLoading,
                error: this.comparisonError || "",
                subItems: this.comparisonSubItems,
                selectedItem: this.comparisonItem,
                years: this.comparisonYears,
                // Granularidad del eje X. El backend siempre manda 12 meses; el
                // chart agrega a quarters cuando el período activo es trimestral.
                period: this.filterPeriod,
            };
        },
    },

    watch: {
        // Un solo watcher para los dos fetches dependientes del mismo conjunto de
        // filtros — antes eran dos signatures idénticas con un watcher cada una.
        fetchSignature() {
            this.fetchSalesData();
            this.fetchSalesCharts({ topN: TOP_CLIENTS_COUNT });
        },
        // El mes del header redefine el cutoff del año en curso de la
        // comparación — refetch sólo si el panel está abierto (el resto de los
        // disparadores —subcategoría, años, sub-filter— se manejan en sus
        // handlers explícitos para no duplicar requests).
        endMonth() {
            if (this.comparisonOpen) this.fetchSalesComparison();
        },
    },

    mounted() {
        // Bootstrap orquestado por la action — el view declara intent ("init
        // del tab Ventas") y el store decide qué se carga en paralelo. Los
        // watchers de fetchSignature/chartsSignature siguen llamando las
        // actions individuales según corresponda.
        this.initVentas({ topN: TOP_CLIENTS_COUNT });
        // classification/getCategories alimenta `categories` + `itemsByCategory`
        // que `subNavFilters` y `headerCategories` consumen. Si el usuario
        // llegó a Ventas por otra ruta (DashboardClassificator ya las cargó),
        // la action hace short-circuit.
        this.getCategories();
        // Drill cross-tab: si el usuario llegó clickeando una celda de Ventas
        // Netas en el P&L (Resultados), abrimos el modal del mes pedido.
        this.consumePendingDrill();
    },

    methods: {
        ...mapActions("salesReport", ["initVentas", "fetchSalesData", "fetchSalesCharts", "fetchSalesComparison"]),
        ...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
        ...mapActions("salesAnalysis", ["fetchSalesAnalysis"]),
        // Reuso del módulo clientDetail (mismo drawer que abre BodySalesAnalysis
        // cuando se clickea un cliente del ranking). Acá lo necesitamos para el
        // short-circuit: en modo "clientes" + L3, saltamos el primer modal y
        // abrimos directo el detalle del cliente.
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),
        // Categorías + items son globales (no específicos de Ventas); el
        // store hace short-circuit si ya están cargados por otro view.
        ...mapActions("classification", ["getCategories"]),
        ...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
        ...mapMutations("salesReport", [
            "SET_ACTIVE_SUB_FILTER",
            "SET_COMPARISON_ITEM",
            "SET_COMPARISON_YEARS",
        ]),
        // Aliaseada: `salesAnalysis` también tiene un `SET_SELECTED_ITEMS` (el del
        // modal, mapeado como `setSelectedItems`). El alias distingue el filtro de
        // items del header del de atributos del drawer en cada call site.
        ...mapMutations("salesReport", { setItemsFilter: "SET_SELECTED_ITEMS" }),
        ...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
        ...mapMutations("salesAnalysis", { resetSalesAnalysis: "RESET" }),

        updateEndMonth(date) {
            this.SET_END_MONTH(date);
        },
        updateDateRange(range) {
            // Dispatch a la action — la acción se encarga de coordinar el set
            // del rango + sincronizar `selectedPeriodRange = "custom"` + endMonth
            // para que cualquier consumer que mire esas keys aisladas (legacy)
            // siga coherente con el rango actual.
            this.setCustomRange(range);
        },
        // El picker de Anual emite el rango de años; estado ortogonal al mes
        // (no toca endMonth/selectedPeriodRange).
        updateYearRange(range) {
            this.setCustomYearRange(range);
        },
        updateActiveSubFilter(filter) {
            this.SET_ACTIVE_SUB_FILTER(filter);
            // La comparación depende del modo: en categoría arranca en la primera
            // subcategoría; en cuentas/clientes no aplica. La mutación es síncrona,
            // así que `comparisonSubItems` ya refleja el nuevo sub-filtro ([] fuera
            // de categoría) — lo leemos en vez de recomputar el predicado a mano.
            const items = this.comparisonSubItems;
            this.SET_COMPARISON_ITEM(items.length ? items[0] : null);
            if (this.comparisonOpen) this.fetchSalesComparison();
        },
        // Filtro multi-select del header: enrutado por un método (como el resto
        // de los eventos del template) en vez de atar la mutación cruda al evento.
        onItemsFilterChange(items) {
            this.setItemsFilter(items);
        },
        // Toggle de los gráficos: el título en el header sticky lo dispara, el
        // cuerpo (CollapsibleSection headless) lo refleja. Persistimos la
        // preferencia para que sobreviva al refresh / cambio de tab.
        toggleCharts() {
            this.chartsExpanded = !this.chartsExpanded;
            writeBoolFlag(CHARTS_EXPANDED_KEY, this.chartsExpanded);
        },
        updateFilterPeriod(period) {
            this.SET_FILTER_PERIOD(period);
        },

        // --- Gráfico de comparación año-contra-año ---
        // Toggle del panel: al abrir fija la subcategoría default (modo
        // categoría) y dispara el fetch lazy. La tabla mantiene su propio
        // estado de apertura; acá sólo lo espejamos para el watcher de mes.
        onToggleComparison(open) {
            this.comparisonOpen = open;
            if (!open) return;
            // YTD/Anual no aplican a la comparación. Si el período activo es uno
            // de esos al abrir el panel, caemos a Mensual para que el gráfico y
            // los chips visibles arranquen en una granularidad válida.
            if (this.filterPeriod !== "month" && this.filterPeriod !== "quarter") {
                this.SET_FILTER_PERIOD("month");
            }
            if (this.isCategoryFilter && !this.comparisonItem
                && this.comparisonSubItems.length) {
                this.SET_COMPARISON_ITEM(this.comparisonSubItems[0]);
            }
            this.fetchSalesComparison();
        },
        onComparisonSelectItem(item) {
            this.SET_COMPARISON_ITEM(item);
            this.fetchSalesComparison();
        },
        onComparisonAddYear(year) {
            this.SET_COMPARISON_YEARS([...this.comparisonYears, year]);
            this.fetchSalesComparison();
        },
        onComparisonRemoveYear(year) {
            this.SET_COMPARISON_YEARS(
                this.comparisonYears.filter((y) => y !== year)
            );
            this.fetchSalesComparison();
        },

        // Refetch del análisis cuando el usuario cambia el mes desde el
        // AnualCalendar del header del drawer. account/groupBy/selectedItems
        // se conservan (vía mapState) — el drawer sigue contextualizado al
        // mismo lente, sólo cambia el período.
        onAnalysisMonthChange(date) {
            const month = moment(date).format("YYYY-MM");
            this.fetchSalesAnalysis({
                month,
                account: this.analysisAccount,
                groupBy: this.analysisGroupBy,
                selectedItems: this.analysisSelectedItems,
            });
        },

        // Cleanup del store al cerrar el drawer.
        onAnalysisClose() {
            this.resetSalesAnalysis();
        },

        // Atajo del modo "clientes" + L3: abre el drawer de detalle del cliente
        // directo (mismo que el segundo modal del flujo de análisis). Le
        // pasamos el monthIso del mes clickeado y el alias del cliente; la
        // action `clientDetail/openClientDetail` resuelve el `ciId` con
        // `resolve_counterpart_by_alias`.
        openClientDetailFromCell({ client, month }) {
            this.openClientDetailAction({
                alias: client,
                documentType: "C",
                monthIso: month,
                obligationName: "Otros",
                level2Name: "Ventas",
            });

            // Config estático (chrome + props) en el helper; acá sumamos lo que
            // depende de la instancia: parent y los listeners. Width default
            // (85%) — es el primer drawer del flujo, no se apila sobre otro.
            this.$buefy.modal.open({
                ...clientDetailModalConfig({ fallbackAlias: client }),
                parent: this,
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },

        // Consume el pendingDrill del store (seteado por ResultadosTab al
        // clickear una celda de Ventas Netas en el P&L). Abre el modal de
        // análisis del mes pedido para todas las cuentas (account null →
        // análisis general del mes, igual que la fila total de la tabla).
        async consumePendingDrill() {
            const drill = this.pendingDrill;
            if (!drill || drill.tab !== "ventas") return;
            this.SET_PENDING_DRILL(null);
            // El modal usa categorías para el groupBy de Distribución — las
            // aseguramos antes de abrir (short-circuit si ya cargaron).
            await this.getCategories();
            // Deep-link desde el Libro Mayor (ícono de gráfico): abre directo el
            // detalle del cliente (por alias) en el mes de referencia.
            if (drill.counterpartAlias) {
                this.openClientDetailFromCell({
                    client: drill.counterpartAlias,
                    month: moment(this.endMonth).format("YYYY-MM"),
                });
                return;
            }
            this.openSalesAnalysis({ monthLabel: drill.monthLabel, account: null });
        },

        openSalesAnalysis({ monthLabel, account }) {
            // El modal es mensual: en quarter/year/ytd abre el ÚLTIMO mes del
            // período clickeado (capeado a endMonth).
            const month = periodLastMonth(monthLabel, this.filterPeriod, this.endMonth);
            if (!month) return;

            // Atajo del modo "clientes": la tabla es una lista plana de clientes,
            // así que cualquier celda con `account` ES un cliente. El análisis
            // del mes (ranking de clientes, distribución de tiendas) sería ruido
            // — el usuario ya identificó el cliente. Saltamos el primer drawer y
            // abrimos directo el detalle del cliente. La fila total (account null)
            // no entra acá y cae al análisis general.
            const sub = this.activeSubFilter;
            if (sub === "clientes" && account) {
                this.openClientDetailFromCell({ client: account, month });
                return;
            }

            // Resto de los casos (cuentas, categorías, total row):
            // el modal se abre como si fuera la celda de "Ventas Netas" de la
            // misma columna — sin drillAccount ni preselección de attribute. El
            // usuario decide después dentro del drawer si quiere acotar a una
            // cuenta puntual via el header. La razón: el card de ranking de
            // clientes y la distribución por categoría ya muestran ese
            // breakdown — pre-filtrar a la cuenta/atributo clickeado fuerza un
            // recorte que el usuario quizás no quería.
            const drillAccount = null;
            const drillSelectedItems = {};
            const monthDate = moment(month, "YYYY-MM").toDate();
            // Capitalizado al modo del chip del diseño ("Abr 2025") — fallback
            // para el header si endMonth no se renderea (el caller ya pasa
            // endMonth, así que en práctica el AnualCalendar lo reemplaza).
            const periodLabel = moment(month, "YYYY-MM")
                .locale("es")
                .format("MMM YYYY")
                .replace(/^./, (c) => c.toUpperCase());

            this.fetchSalesAnalysis({
                month,
                account: drillAccount,
                groupBy: this.analysisGroupByForOpen,
                selectedItems: drillSelectedItems,
            });
            this.$buefy.modal.open({
                ...salesAnalysisModalConfig({
                    periodLabel,
                    endMonth: monthDate,
                    getNow: this.getNow,
                }),
                parent: this,
                events: {
                    closed: this.onAnalysisClose,
                    "update-end-month": this.onAnalysisMonthChange,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
.ventas_tab {
    position: relative;
    height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS de los
// sub-filters dinámicos (Cuentas/Categorías/Clientes) inyectados vía
// el slot #center-sub-nav — re-usan las classes .sub_nav_filters/.filter_btn
// que TabHeader expone con scoped styles propios, así que los repetimos
// localmente para que apliquen al markup del slot.

.sub_nav_filters {
    display: flex;
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 8px;
    width: fit-content;
}

.filter_btn {
    padding: 6px 16px;
    border: none;
    background: transparent;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: var(--report-text-muted-strong);
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: var(--report-filter-wash, var(--report-surface));
    }

    &.is-active {
        background: var(--report-filter-accent, var(--report-primary));
        color: var(--report-bg);

        &:hover {
            background: var(--report-filter-accent-hover, var(--report-primary-hover));
        }
    }

    // Categoría sin items clasificados: no seleccionable (la vista sería
    // degenerada). Atenuado + sin hover.
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;

        &:hover {
            background: transparent;
        }
    }
}
</style>
